/**
 * "Esta cobrança existe mesmo no gateway?" — fonte única.
 *
 * A pergunta aparece em dois momentos opostos do ciclo de vida do boleto:
 *
 *   - na EMISSÃO, para decidir se a fatura pode avançar de status;
 *   - no CANCELAMENTO, para decidir se vale chamar o Sicoob.
 *
 * Os dois erravam de formas diferentes pelo mesmo motivo — cada tela tinha a sua
 * versão da regra:
 *
 * 1. Em 31/07/2026 o Sicoob passou ~2h devolvendo 500/5001. O n8n seguia mesmo
 *    assim, gravava a linha digitável vazia e desenhava o PDF, então `url_boleto`
 *    ficava preenchida e a emissão passava como boa. Resultado: 14 boletos sem
 *    código de barras nem PIX, 3 já enviados ao cliente.
 * 2. Cancelar um desses boletos mandava `nossoNumero: null` para o n8n, que
 *    montava `.../boletos/null/baixar`. O gateway do Sicoob respondia
 *    `400 {"httpCode":"400","httpMessage":"Bad Request","moreInformation":
 *    "The request URI was invalid."}` — erro de URI, não de negócio — e o
 *    operador ficava sem conseguir cancelar (chamado da UC 346058301294,
 *    fatura 9404, JUL/2026).
 *
 * ⚠️ O que prova a cobrança no Sicoob é o **nosso número + a linha digitável**,
 * nunca a `url_boleto`: o PDF é desenhado depois do registro e sai igual mesmo
 * quando o registro falhou.
 *
 * ⚠️ O recorte por parceira é obrigatório. A trava original de integridade
 * ignorou isso e barrou toda emissão de parceira — 26 cobranças duplicadas na
 * conta da Gran, R$ 28.449,26, até 24/08/2026 (ver migration 20260824150000).
 * No Asaas não há linha digitável nesse momento; o que prova é o id da cobrança
 * ou o PDF já publicado.
 *
 * Módulo sem I/O de propósito: a regra tem teste próprio e é espelhada pelo
 * trigger `fn_checar_integridade_boleto`, que é a barreira que também alcança o
 * n8n (o front sozinho não alcança).
 */

export interface FaturaEmissao {
    num_cobranca_boleto?: string | number | null;
    linha_digitavel_boleto?: string | null;
    url_boleto?: string | null;
    empresa_parceira_id?: string | number | null;
}

export interface ResultadoEmissao {
    /** true quando a cobrança existe de fato no gateway. */
    ok: boolean;
    /** Vazio quando `ok`. Texto pronto para a tela, dizendo o que o operador faz agora. */
    motivo: string;
}

const preenchido = (v: unknown): boolean =>
    v !== null && v !== undefined && String(v).trim() !== '';

/** true quando a fatura corre pelo gateway de empresa parceira (Asaas/Gran). */
const ehParceira = (f: FaturaEmissao): boolean => preenchido(f.empresa_parceira_id);

export const MOTIVO_SICOOB_NAO_REGISTROU =
    'O boleto não foi registrado no Sicoob (sem nosso número / linha digitável). '
    + 'A fatura continua em "Com boleto calculado" e nenhuma cobrança foi criada — pode emitir de novo sem risco de duplicar. '
    + 'Confira CPF/CNPJ, CEP e endereço do cliente; se estiverem corretos, é instabilidade do Sicoob: aguarde alguns minutos e emita de novo por esta mesma tela. '
    + 'Não envie o PDF ao cliente enquanto isso — ele sai sem código de barras e sem PIX.';

/**
 * Nosso número veio, linha digitável não. Estado ambíguo e o mais perigoso dos três:
 * o registro pode ter acontecido no Sicoob e só a resposta ter vindo pela metade.
 * Reemitir às cegas aqui é como nasceram as cobranças duplicadas de 24/08/2026 —
 * por isso a instrução é cancelar primeiro, o que baixa o título se ele existir.
 */
export const MOTIVO_SICOOB_PARCIAL =
    'O Sicoob devolveu o nosso número mas não a linha digitável, então o boleto saiu sem código de barras e sem PIX. '
    + 'PODE existir cobrança registrada no banco: não emita de novo direto, isso arriscaria cobrar o cliente duas vezes. '
    + 'Abra a fatura, cancele o boleto (o cancelamento baixa o título se ele existir) e só então emita de novo. '
    + 'Não envie o PDF ao cliente — ele não é pagável.';

export const MOTIVO_ASAAS_NAO_CRIOU =
    'A cobrança não foi criada no Asaas. Nenhum valor foi cobrado do cliente — pode emitir de novo. '
    + 'Confira se CPF/CNPJ, CEP e demais dados do cliente estão preenchidos e válidos, corrija na UC e emita de novo por esta mesma tela.';

/**
 * Decide se a emissão pode avançar de status.
 *
 * Sandbox fica de fora: a edge function `emitir-boleto-teste` preenche a fatura
 * com dados fictícios e já falha alto se der errado — não há banco para provar.
 */
export function emissaoBoletoValida(
    fatura: FaturaEmissao,
    opcoes: { sandbox?: boolean } = {},
): ResultadoEmissao {
    if (opcoes.sandbox) return { ok: true, motivo: '' };

    if (ehParceira(fatura)) {
        return preenchido(fatura.num_cobranca_boleto) || preenchido(fatura.url_boleto)
            ? { ok: true, motivo: '' }
            : { ok: false, motivo: MOTIVO_ASAAS_NAO_CRIOU };
    }

    // O que decide é a LINHA DIGITÁVEL: é dela que saem o código de barras e o PIX.
    //
    // ⚠️ Nosso número ausente COM linha digitável passa. Exigir os dois barrou a
    // fatura 6377 no teste de 08/09/2026 — boleto legado e pagável. São 500+ faturas
    // com esse padrão, e barrar levaria o operador a reemitir por cima de uma
    // cobrança que existe. Falta de nosso número é outro defeito (não dá para baixar
    // no Sicoob) e o cancelamento já trata: baixa só no VigorHub.
    if (preenchido(fatura.linha_digitavel_boleto)) {
        return { ok: true, motivo: '' };
    }

    // Falhou — mas o conselho muda conforme a cobrança possa ou não existir no banco.
    return {
        ok: false,
        motivo: preenchido(fatura.num_cobranca_boleto)
            ? MOTIVO_SICOOB_PARCIAL
            : MOTIVO_SICOOB_NAO_REGISTROU,
    };
}

/**
 * Existe título no gateway para dar baixa?
 *
 * Pergunta mais estreita que `emissaoBoletoValida`: aqui não importa se o boleto
 * ficou pagável, e sim se há identificador para o Sicoob baixar. Sem nosso
 * número não há o que cancelar no banco — chamar a API só produz o 400 de URI
 * inválida, e o cancelamento se resolve inteiro no VigorHub.
 */
export function boletoExisteNoGateway(fatura: FaturaEmissao): boolean {
    return preenchido(fatura.num_cobranca_boleto);
}

export const MOTIVO_CANCELAMENTO_LOCAL =
    'Este boleto nunca chegou a ser registrado no Sicoob (ficou sem código de barras e sem PIX), '
    + 'então não há o que cancelar no banco: ele foi cancelado apenas no VigorHub. '
    + 'Se ainda precisa cobrar este mês, emita um boleto novo por esta tela.';

/**
 * Já existe boleto VIVO nesta fatura?
 *
 * A fatura guarda UM `num_cobranca_boleto`, sobrescrito a cada emissão. O desenho
 * pressupõe que toda reemissão passe antes pelo cancelamento — que é o que zera o
 * campo. Nada garantia isso, e o campo sobrescrito apagava o rastro do boleto
 * anterior, que continuava vivo no Sicoob e invisível para o VigorHub.
 *
 * Caso que originou (09/09/2026, fatura 11172, UC 98279801226, SET/2026): boleto
 * 5704 emitido às 12:33, 5705 às 12:39, `historico_cancelamentos` vazio. O 5704
 * seguiu em aberto e o associado o pagou pelo DDA — a fatura apontava para o 5705.
 *
 * Não foi erro do operador: ao emitir, o modal reabria com os dados antigos (o
 * refetch não era aguardado) e com o botão já destravado, então a tela parecia não
 * ter feito nada. Os intervalos entre a 1ª e a 2ª emissão dos casos afetados na base
 * — 12s, 12s, 18s, 54s — são reclique, não decisão. Levantamento de 21/09/2026:
 * 11 faturas com mais emissões que cancelamentos, ~R$ 14,7 mil em boletos órfãos.
 *
 * A regra vale para qualquer gateway: no Asaas o `num_cobranca_boleto` guarda o id
 * da cobrança e reemitir por cima deixa a anterior viva do mesmo jeito.
 */
export function boletoJaRegistrado(fatura: FaturaEmissao): boolean {
    return preenchido(fatura.num_cobranca_boleto);
}

export function motivoBoletoJaRegistrado(nossoNumero: unknown): string {
    return `Esta fatura já tem a cobrança ${String(nossoNumero).trim()} registrada no banco. `
        + 'NÃO emita de novo: o boleto anterior continuaria vivo e o cliente poderia pagá-lo '
        + '(inclusive por DDA), enquanto o sistema só enxergaria o novo. '
        + 'Se precisa trocar o boleto, abra a fatura e cancele a cobrança atual primeiro — '
        + 'o cancelamento baixa o título no banco e libera a emissão. '
        + 'Se você acabou de emitir e a tela não atualizou, recarregue a página: o boleto já saiu.';
}

// --- Boleto vivo parado numa etapa anterior à emissão.
//
// Caso que originou (16/10/2026, fatura 14192, UC 410067701219, OUT/2026): o Ítalo
// emitiu o boleto 6816 às 13:26 e a fatura foi para "A enviar". Às 13:30 a Alice, com a
// tela aberta desde antes, validou e calculou a mesma fatura — e cada passo regravou o
// status sem olhar onde ela já estava. A fatura voltou para "Com boleto calculado" com
// o boleto vivo: fora da fila de envio (o cliente nunca recebeu) e presa na janela de
// emissão, que só dizia "NÃO emita de novo" sem dar saída.
//
// Na mesma situação, por outro caminho, ficaram as faturas 14454 e 14032 (OUT/2026):
// o banco registrou o boleto, mas a emissão não terminou na tela.

/**
 * Etapas do quadro anteriores à emissão. Fatura com boleto vivo não pode estar em
 * nenhuma delas: ninguém a envia ao cliente e não há onde cancelá-la.
 *
 * Espelhada pelo trigger `fn_barrar_volta_etapa_com_boleto_vivo`. Ao mudar uma, mude a outra.
 */
export const ETAPAS_ANTES_DA_EMISSAO: readonly string[] = [
    'Aguardando Upload',
    'Aguardando processamento',
    'Aguardando validação',
    'Fatura disponível',
    'Fatura validada',
    'Com boleto calculado',
];

/** Etapa em que a fatura fica depois que o boleto existe: dali sai o envio e o cancelamento. */
export const ETAPA_A_ENVIAR = 'Com boleto emitido / A enviar';

export interface BoletoDaFatura extends FaturaEmissao {
    status_fatura?: string | null;
    codigo_barras_boleto_asaas?: string | null;
    valor_emissao_boleto_unico?: number | string | null;
    valor_boleto_vigor?: number | string | null;
    vencimento_boleto?: string | null;
    emitido_por_nome?: string | null;
    emitido_em?: string | null;
    ambiente?: string | null;
}

/** Boleto vivo numa etapa anterior à emissão: a fatura precisa ir para "A enviar". */
export function boletoParadoAntesDoEnvio(f: BoletoDaFatura | null | undefined): boolean {
    if (!f || !boletoJaRegistrado(f)) return false;
    return ETAPAS_ANTES_DA_EMISSAO.includes(String(f.status_fatura ?? '').trim());
}

/**
 * Valor que o banco registrou no boleto, em reais. Padrão FEBRABAN: na linha digitável
 * (47 dígitos) são os 10 últimos; no código de barras (44), do 10º ao 19º. Vale para o
 * Sicoob e para o Asaas.
 *
 * null quando não dá para ler, ou quando o boleto saiu sem valor no código (dez zeros).
 */
export function valorRegistradoNoBoleto(codigo: unknown): number | null {
    const d = String(codigo ?? '').replace(/\D/g, '');
    let centavos: string;
    if (d.length === 47) centavos = d.slice(37);
    else if (d.length === 44) centavos = d.slice(9, 19);
    else return null;
    const n = Number(centavos);
    return Number.isFinite(n) && n > 0 ? n / 100 : null;
}

/** O que a fatura manda cobrar: o total do boleto único, ou só a parte da Vigor. */
export function valorACobrarDaFatura(f: BoletoDaFatura): number | null {
    for (const v of [f.valor_emissao_boleto_unico, f.valor_boleto_vigor]) {
        const n = Number(v);
        if (v !== null && v !== undefined && v !== '' && Number.isFinite(n) && n > 0) {
            return Math.round(n * 100) / 100;
        }
    }
    return null;
}

export interface ConferenciaValorBoleto {
    valorBoleto: number | null;
    valorFatura: number | null;
    /** null quando um dos dois não pôde ser lido. Diferença de 1 centavo conta como igual. */
    bate: boolean | null;
}

/**
 * O boleto registrado ainda cobra o que a fatura calcula? Se alguém recalculou depois
 * da emissão, não — e esse boleto não pode ser enviado: tem de ser cancelado e emitido
 * de novo.
 */
export function conferirValorDoBoleto(f: BoletoDaFatura): ConferenciaValorBoleto {
    const valorBoleto = valorRegistradoNoBoleto(f.linha_digitavel_boleto)
        ?? valorRegistradoNoBoleto(f.codigo_barras_boleto_asaas);
    const valorFatura = valorACobrarDaFatura(f);
    if (valorBoleto === null || valorFatura === null) return { valorBoleto, valorFatura, bate: null };
    return {
        valorBoleto,
        valorFatura,
        bate: Math.abs(Math.round(valorBoleto * 100) - Math.round(valorFatura * 100)) <= 1,
    };
}

export type AcaoQueVoltaEtapa = 'validar' | 'calcular' | 'retroceder';

const VERBO_ACAO: Record<AcaoQueVoltaEtapa, string> = {
    validar: 'Validar',
    calcular: 'Calcular',
    retroceder: 'Retroceder',
};

/** Recusa de validar/calcular/retroceder uma fatura que já tem boleto vivo. */
export function motivoBoletoVivoNaoVoltaEtapa(acao: AcaoQueVoltaEtapa, f: BoletoDaFatura): string {
    const quem = f.emitido_por_nome ? ` por ${String(f.emitido_por_nome).trim()}` : '';
    return `Esta fatura já tem o boleto ${String(f.num_cobranca_boleto ?? '').trim()} emitido${quem}. `
        + 'Provavelmente outra pessoa terminou esta fatura enquanto a sua tela estava aberta. '
        + `${VERBO_ACAO[acao]} agora a tiraria da fila de envio, e o cliente ficaria sem receber o boleto. `
        + 'Nada foi alterado: feche esta janela e recarregue a página. '
        + 'Se o boleto precisa mudar, cancele-o na fatura (em "A enviar") e só depois refaça o cálculo.';
}
